/**
 * Invoices domain API functions (Step 7.1.4).
 *
 * Invoice list/fetch with tenant filter and pagination normalization.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  adminErrorFrom,
  getApiHeaders,
  expectPaginatedItems,
  parseAdminErrorResponse,
} from "./core";

/**
 * Fetch invoices (GET /api/invoices).
 * Without args: all org invoices; returns items array (paginated shape normalized).
 * With { tenantId, limit, skip }: filtered by tenant; returns full JSON
 * { items, total, skip, limit }.
 *
 * `skip` IS OPTIONAL AND OMITTED FROM THE URL WHEN NOT GIVEN (TINV-2). The route
 * has always accepted it — `skip: int = Query(0, ge=0)` — and has always echoed
 * it back in the envelope; the client simply never offered it, which is why the
 * tenant-detail tab used to page a loaded window in the browser instead of asking
 * the server for page two. Exposing it is what lets a caller paginate over the
 * SAME set `total` counts.
 *
 * Left absent rather than defaulted to `0` on purpose: every existing caller
 * passes no `skip`, and sending `&skip=0` for them would change a request that is
 * currently correct, for no gain. Absent means the route's own default applies,
 * which is the same zero.
 */
export async function fetchAdminInvoices(options) {
  if (
    options != null &&
    typeof options === "object" &&
    options.tenantId != null &&
    options.tenantId !== ""
  ) {
    const { tenantId, limit = 20, skip } = options;
    const skipParam = skip == null ? "" : `&skip=${encodeURIComponent(skip)}`;
    const url = `${API_BASE_URL}/api/invoices?tenant_id=${encodeURIComponent(tenantId)}&limit=${limit}${skipParam}`;
    const res = await fetch(url, { headers: getApiHeaders() });
    if (!res.ok) {
      throw new Error("Failed to fetch invoices");
    }
    return res.json();
  }
  const res = await fetch(`${API_BASE_URL}/api/invoices`, { headers: getApiHeaders() });
  if (!res.ok) {
    throw new Error("Rechnungen konnten nicht geladen werden.");
  }
  const data = await res.json();
  return expectPaginatedItems(data, "GET /api/invoices");
}

/**
 * Generate (or force-regenerate) the PDF for an invoice.
 * POST /api/admin/invoices/{invoiceId}/pdf/generate
 *
 * Returns the InvoicePdfPersistenceResult dict:
 *   { object_key, size_bytes, generated_at, qr_reference,
 *     skipped, replaced_old_key }
 */
export async function generateAdminInvoicePdf(
  invoiceId,
  { force = false } = {}
) {
  const qs = force ? "?force=true" : "";
  const url =
    `${API_BASE_URL}/api/admin/invoices/` +
    `${encodeURIComponent(invoiceId)}/pdf/generate${qs}`;
  const res = await fetch(url, {
    method: "POST",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF konnte nicht generiert werden.");
  }
  return res.json();
}

/**
 * Fetch a short-lived presigned R2 URL for the invoice PDF.
 * GET /api/admin/invoices/{invoiceId}/pdf/url
 *
 * Returns a string (the URL itself), unwrapped from { url: ... }.
 */
export async function fetchAdminInvoicePdfUrl(invoiceId) {
  const url =
    `${API_BASE_URL}/api/admin/invoices/` +
    `${encodeURIComponent(invoiceId)}/pdf/url`;
  const res = await fetch(url, { headers: getApiHeaders() });
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "PDF-Link konnte nicht erstellt werden.");
  }
  const data = await res.json();
  return data?.url || "";
}

/**
 * Create an invoice with nested line items (Phase 3B.1b/3B.2).
 * POST /api/admin/invoices
 *
 * Body: { tenancy_id, billing_year, billing_month, items: [...] }
 * Returns: InvoiceDetailResponse (201 Created).
 *
 * On non-2xx, throws an Error with ``status`` attached so the wizard
 * can branch on 409 (duplicate), 422 (validation), 429 (rate limit),
 * 404 (tenancy missing), etc. 429 gets a fixed German message because
 * slowapi's default body shape is not the ``{detail: string}`` that
 * ``parseAdminErrorResponse`` expects.
 */
export async function createAdminInvoice(payload) {
  const res = await fetch(`${API_BASE_URL}/api/admin/invoices`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail;
    if (res.status === 429) {
      detail = "Zu viele Versuche. Bitte 1 Minute warten.";
    } else {
      detail = await parseAdminErrorResponse(res);
    }
    const err = new Error(detail || "Rechnung konnte nicht erstellt werden.");
    err.status = res.status;
    throw err;
  }
  return res.json();
}


/**
 * Fetch the org's invoice capabilities (GET /api/admin/invoices/capabilities).
 *
 * Answers ONE question: may Vantio's own engine still create invoices for this
 * organization? Shape:
 *
 *   { invoice_creation_provider: "native" | "bexio",
 *     native_invoice_creation_enabled: boolean }
 *
 * WHY NOT `GET /api/admin/billing-settings`. That endpoint carries the same
 * provider field, but it is admin-only — and managers may create invoices too, so
 * gating their buttons on an endpoint they cannot call would leave them clicking a
 * control that 409s. This route exists precisely so both roles can ask.
 *
 * The response is returned unchanged. Reshaping it here would put a second opinion
 * about the capability between the backend and the components.
 */
export async function fetchInvoiceCapabilities() {
  const res = await fetch(`${API_BASE_URL}/api/admin/invoices/capabilities`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error("Rechnungs-Berechtigungen konnten nicht geladen werden.");
  }
  return res.json();
}


/**
 * Cancel ONE invoice safely (Slice 2B/2C).
 * POST /api/admin/invoices/{invoiceId}/cancel
 *
 * The ONLY way to cancel an invoice. The generic
 * `PUT /api/invoices/{id}/status?status=cancelled` refuses since Slice 2C: it
 * would write the string alone, leaving no timestamp, no reason and no audit
 * entry, and it never asked whether the invoice had been paid.
 *
 * Returns the full InvoiceDetailResponse, so a caller can put the answer
 * straight into its invoice state instead of refetching.
 *
 * Errors travel through `adminErrorFrom`, which keeps the server's own
 * `detail.code` on `err.code` beside the German sentence on `err.message`.
 * Branch on the CODE — `invoice_paid`, `payment_recorded`, `already_cancelled`,
 * `unknown_status`, `audit_parent_unresolvable` — never on the text, which is
 * free to be improved without warning.
 *
 * The reason is sent as given; trimming and the 3..500 bounds are the caller's
 * courtesy and the server's rule, and the server is the one that decides.
 */
export async function cancelAdminInvoice(invoiceId, cancellationReason) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/invoices/${encodeURIComponent(invoiceId)}/cancel`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ cancellation_reason: cancellationReason }),
    }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}
